import { Button } from "@faire/web--source/slate/Button";
import { Column } from "@faire/web--source/slate/Layout";
import { Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import brandPortalOrders from "@faire/web-api--source/routes/www/brand-portal/orders";
import React, { type JSX } from "react";

import { Bold } from "../common/Bold";
import { LiItem } from "../common/LiItem";

export const CannotFindAnOrderIA: React.FC = () => {
  return (
    <Column>
      <Typography>
        <StrictLocalMsg
          defaultMessage="If you’re having trouble finding a retailer’s order in your Faire account, it might be due to a few different reasons. Here are some steps you can take to address this issue:"
          description={{
            text: "A step-by-step guide to cannot find an order",
          }}
        />
      </Typography>
      <Spacer height="1x" />
      <ol>
        <LiItem step={1}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="<BoldFormatting>Order still processing:</BoldFormatting>"
              values={{
                BoldFormatting: (chunks: JSX.Element) => BoldFormatting(chunks),
              }}
              description={{
                text: "The first step title in the cannot find an order guide",
              }}
            />
          </Typography>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Have the retailer provide any additional information they might have about their account and encourage them to contact us directly for further assistance."
              description={{
                text: "The first step content in the cannot find an order guide",
              }}
            />
          </Typography>
        </LiItem>
        <Spacer height="1x" />
        <LiItem step={2}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="<BoldFormatting>Different order numbers:</BoldFormatting>"
              values={{
                BoldFormatting: (chunks: JSX.Element) => BoldFormatting(chunks),
              }}
              description={{
                text: "The second step title in the cannot find an order guide",
              }}
            />
          </Typography>
          <Typography>
            <StrictLocalMsg
              defaultMessage="If a retailer gives you an order number that doesn’t appear on your <LinkFormatting>Orders</LinkFormatting> page, ask them for their <BoldFormatting>invoice number</BoldFormatting> instead. The invoice number should match the order number you see, allowing you to locate the order."
              values={{
                LinkFormatting: (chunks: JSX.Element) =>
                  OrderLinkFormatting(chunks),
                BoldFormatting: (chunks: JSX.Element) => BoldFormatting(chunks),
              }}
              description={{
                text: "The second step content in the cannot find an order guide",
              }}
            />
          </Typography>
        </LiItem>
        <Spacer height="1x" />
        <LiItem step={3}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="<BoldFormatting>New accounts under review:</BoldFormatting>"
              values={{
                BoldFormatting: (chunks: JSX.Element) => BoldFormatting(chunks),
              }}
              description={{
                text: "The third step title in the cannot find an order guide",
              }}
            />
          </Typography>
          <Typography>
            <StrictLocalMsg
              defaultMessage="If a retailer has provided their invoice number but you’re still unable to find their order, it’s possible that the retailer has a new account that’s on hold while our team reviews their information. This review process should take no more than a few days."
              description={{
                text: "The third step content in the cannot find an order guide",
              }}
            />
          </Typography>
        </LiItem>
      </ol>
      <Spacer height="1x" />
      <Typography>
        <StrictLocalMsg
          defaultMessage="If you’ve checked the above and still can’t locate the order, contact us for further assistance. We’re here to help resolve these issues."
          description={{
            text: "The final step content in the cannot find an order guide",
          }}
        />
      </Typography>
    </Column>
  );
};

const OrderLinkFormatting = (chunks: JSX.Element) => (
  <Button variant="tertiary" linkTo={brandPortalOrders()} target="_blank">
    <Bold>{chunks}</Bold>
  </Button>
);

const BoldFormatting = (chunks: JSX.Element) => <Bold>{chunks}</Bold>;
